import { useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { findProfile } from "@/reducers/profilesSliceSelectors"
import ChatHelpers from "@/lib/chatHelpers"
import {
  extractDriverInitialsFromChatName,
  extractDriverNameFromChatName,
} from "@/lib/chatNameInitials"
import { participantPhotoToSrc } from "@/components/AvatarImage"

type ForeignChatAvatarData = {
  /** Photo URL if the driver profile is loaded, otherwise undefined */
  photo: string | undefined
  /** Initials to display - from profile name or derived from chat name */
  initials: string | undefined
  /** The driver's name behind those initials, for components that build their
   * own fallback from a name rather than taking initials ready-made. */
  name: string | undefined
  /** Avatar color - from account chat configuration */
  color: string | undefined
}

/**
 * Hook to get avatar data for a foreign (other dispatcher's) chat.
 * Tries to use the driver's profile if available, otherwise derives
 * initials from the chat name.
 */
export const useForeignChatAvatar = (
  jid: string,
  chatName: string,
): ForeignChatAvatarData => {
  const accountChatSelector = useMemo(() => selectAccountChatByJid(jid), [jid])
  const accountChat = useAppSelector(accountChatSelector)
  const driverJid = accountChat?.driverA
  const driverProfile = useAppSelector(findProfile(driverJid))

  return useMemo(() => {
    // Try to use the driver profile if available
    if (driverProfile) {
      const photo = participantPhotoToSrc(driverProfile.photo)
      const initials =
        `${driverProfile.firstName?.charAt(0) ?? ""}${driverProfile.lastName?.charAt(0) ?? ""}`.toUpperCase()
      const name = [driverProfile.firstName, driverProfile.lastName]
        .filter(Boolean)
        .join(" ")
      const color = accountChat
        ? ChatHelpers.getAccountChatColor(accountChat)
        : undefined

      return {
        photo,
        initials: initials || undefined,
        name: name || driverProfile.name || undefined,
        color,
      }
    }

    // Fall back to deriving initials from chat name
    const initials = extractDriverInitialsFromChatName(chatName)
    const color = accountChat
      ? ChatHelpers.getAccountChatColor(accountChat)
      : undefined

    return {
      photo: undefined,
      initials,
      name: extractDriverNameFromChatName(chatName),
      color,
    }
  }, [driverProfile, chatName, accountChat])
}
